import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
} from "@mui/material";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";

import useSSL from "../../../../hooks/super/useSSL";
import { formatDate } from "../../../../utils/format";
import { getComparator, stableSort } from "../../../../utils/tableSort";
import {
  TOKENS,
  EmptyState,
} from "../../../../components/pages/_new/super/design";

const headCells = [
  { id: "api_key", label: "Api Key", sortable: true },
  { id: "updated", label: "Updated", sortable: true },
];

const HeaderCell = ({ children, sortable, id, orderBy, order, onSort }) => (
  <TableCell
    sx={{
      padding: "10px 14px",
      borderBottom: `1px solid ${TOKENS.border}`,
      background: TOKENS.bgSunken,
      color: TOKENS.fg2,
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    }}
  >
    {sortable ? (
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : "asc"}
        onClick={() => onSort(id)}
        sx={{
          color: `${TOKENS.fg2} !important`,
          "& .MuiTableSortLabel-icon": { color: `${TOKENS.fg2} !important` },
        }}
      >
        {children}
      </TableSortLabel>
    ) : (
      children
    )}
  </TableCell>
);

const DataCell = ({ children }) => (
  <TableCell
    sx={{
      padding: "12px 14px",
      borderBottom: `1px solid ${TOKENS.borderSubtle}`,
      color: TOKENS.fg1,
      fontSize: 13.5,
    }}
  >
    {children}
  </TableCell>
);

function ZeroSSLApiKeyHistoryList() {
  const { apis, apiTotal, apiFrom, apiSize, getApiKeyHistory } = useSSL();
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("updated");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  React.useEffect(() => {
    setPage(apiFrom / apiSize);
  }, [apiFrom]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    setRowsPerPage(apiSize);
  }, [apiSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (id) => {
    if (id === orderBy) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  const sorted = React.useMemo(
    () => stableSort(Array.isArray(apis) ? apis : [], getComparator(order, orderBy)),
    [apis, order, orderBy]
  );
  const isLoading = apis === null;
  const isEmpty = !isLoading && sorted.length === 0;
  const last = Math.max(0, Math.ceil(apiTotal / rowsPerPage) - 1);

  return (
    <Box
      sx={{
        border: `1px solid ${TOKENS.border}`,
        borderRadius: `${TOKENS.radiusM}px`,
        background: TOKENS.bgSurface,
        boxShadow: TOKENS.shadowXs,
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              {headCells.map((c) => (
                <HeaderCell
                  key={c.id}
                  id={c.id}
                  sortable={c.sortable}
                  orderBy={orderBy}
                  order={order}
                  onSort={handleSort}
                >
                  {c.label}
                </HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: rowsPerPage }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {headCells.map((__, j) => (
                    <DataCell key={`sk-${i}-${j}`}>
                      <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1 }} />
                    </DataCell>
                  ))}
                </TableRow>
              ))
            ) : isEmpty ? (
              <TableRow>
                <TableCell colSpan={headCells.length} sx={{ borderBottom: "none" }}>
                  <EmptyState
                    icon={<KeyOutlinedIcon />}
                    title="No history"
                    body="Saved Zero SSL API keys will appear here."
                  />
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((row, i) => (
                <TableRow
                  key={`api-${i}`}
                  hover
                  sx={{ "&:hover": { background: TOKENS.bgSunken } }}
                >
                  <DataCell>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: TOKENS.fg1,
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 480,
                      }}
                    >
                      {row?.api_key || "—"}
                    </Typography>
                  </DataCell>
                  <DataCell>
                    <Typography sx={{ fontSize: 12.5, color: TOKENS.fg1 }}>
                      {row?.updated ? formatDate(row.updated) : "—"}
                    </Typography>
                  </DataCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!isLoading && apiTotal > 0 ? (
        <Box
          sx={{
            p: "10px 16px",
            borderTop: `1px solid ${TOKENS.borderSubtle}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2 }}>
            Page {page + 1} of {Math.max(1, last + 1)}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography
            component="button"
            type="button"
            onClick={() => {
              if (page > 0) {
                setPage(page - 1);
                getApiKeyHistory(rowsPerPage, rowsPerPage * (page - 1), false);
              }
            }}
            disabled={page === 0}
            sx={{
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.bgSurface,
              color: page === 0 ? TOKENS.fg3 : TOKENS.fg1,
              fontSize: 12.5,
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              cursor: page === 0 ? "not-allowed" : "pointer",
            }}
          >
            Prev
          </Typography>
          <Typography
            component="button"
            type="button"
            onClick={() => {
              if (page < last) {
                setPage(page + 1);
                getApiKeyHistory(rowsPerPage, rowsPerPage * (page + 1), false);
              }
            }}
            disabled={page >= last}
            sx={{
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.bgSurface,
              color: page >= last ? TOKENS.fg3 : TOKENS.fg1,
              fontSize: 12.5,
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              cursor: page >= last ? "not-allowed" : "pointer",
            }}
          >
            Next
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

export default ZeroSSLApiKeyHistoryList;
